/* eslint-disable consistent-return */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import cx from 'classnames';
import {
  FC, ReactNode, useEffect, useRef, useState,
} from 'react';

import CloseIcon from '../../SVGIcons/CloseIcon';
import { Button } from '../Button';
import { Transition } from '../Transition/Transition';
import classes from './Modal.module.scss';

const DURATION = 200;

interface ModalProps {
  onClose: () => void;
  onClear?: () => void;
  className?: string;
  paperClassName?: string;
  bodyClassName?: string;
  headerClassName?: string;
  title?: ReactNode;
  children: ReactNode;
  isOpen: boolean;
  isShowHeader?: boolean;
  isShowClear?: boolean;
  fullHeight?: boolean;
  bottomButton?: {
    text: string;
    href?: string;
    onClick?: () => void;
  };
}

export const Modal: FC<ModalProps> = ({
  onClose,
  children,
  onClear,
  className = '',
  paperClassName,
  bodyClassName,
  headerClassName,
  title,
  isOpen,
  isShowHeader = false,
  bottomButton,
  isShowClear = true,
  fullHeight = false,
}) => {
  const [mounted, setMounted] = useState(isOpen);
  const mountTimerIdRef = useRef(0);

  // handle escape button
  useEffect(() => {
    if (isOpen) {
      const handleEscButton = (e: KeyboardEvent) => {
        if (e.code === 'Escape') {
          onClose();
        }
      };
      document.addEventListener('keydown', handleEscButton);
      return () => document.removeEventListener('keydown', handleEscButton);
    }
  }, [isOpen]);

  // handle document body scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = 'auto';
      };
    }
    document.body.style.overflow = 'auto';
  }, [isOpen]);

  // handle mounting
  useEffect(() => {
    clearTimeout(mountTimerIdRef.current);
    if (!isOpen) {
      mountTimerIdRef.current = window.setTimeout(() => {
        setMounted(false);
      }, DURATION);
    } else {
      setMounted(true);
    }
  }, [isOpen]);

  if (!mounted) {
    return null;
  }

  return (
    <div className={classes.modal}>
      <Transition
        in={isOpen}
        duration={DURATION}
        onClick={onClose}
        className={classes.modal__mask}
        classNames={{
          entered: classes['modal__mask--entered'],
          entering: classes['modal__mask--entering'],
          exited: classes['modal__mask--exited'],
          exiting: classes['modal__mask--exiting'],
        }}
      />
      <Transition
        in={isOpen}
        duration={DURATION}
        className={cx(classes['modal__paper-wrapper'], className)}
        classNames={{
          entered: classes['modal__paper-wrapper--entered'],
          entering: classes['modal__paper-wrapper--entering'],
          exited: classes['modal__paper-wrapper--exited'],
          exiting: classes['modal__paper-wrapper--exiting'],
        }}
      >
        <div
          className={cx(classes.modal__paper, paperClassName, {
            [classes['modal__paper--with-header']]: isShowHeader,
            [classes['modal__paper--full-height']]: fullHeight,
          })}
        >
          {isShowHeader && (
            <div className={cx(classes.modal__header, headerClassName)}>
              <div className={classes['modal__header-title']}>
                <CloseIcon
                  role="none"
                  className={classes['modal__header-close-icon']}
                  onClick={onClose}
                  fill="black"
                />
                <p>{title}</p>
              </div>
              {isShowClear ? (
                <button
                  className={cx(classes['modal__header-clear-button'])}
                  type="button"
                  onClick={onClear}
                >
                  Очистить
                </button>
              ) : null}
            </div>
          )}
          <div className={cx(classes.modal__body, bodyClassName)}>
            {children}
          </div>
          {bottomButton && (
            <div className={classes.modal__footer}>
              <Button
                className={classes['modal__footer-button']}
                type={bottomButton.href ? 'a' : 'button'}
                href={bottomButton.href}
                onClick={bottomButton.onClick}
                variant="secondary"
              >
                {bottomButton.text}
              </Button>
            </div>
          )}
        </div>
      </Transition>
    </div>
  );
};
